import { useState } from "react";
import { Check, ShieldCheck, AlertTriangle } from "lucide-react";
import { usePal, PalHead, SubHead, PalTabs, TabPanel, PalIcon } from "./PalUI";
import PalEconomics from "./PalEconomics";

const Inventory = ({ a }) => {
  const { ink, dim, hair } = usePal();
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-[14px]" data-testid="pal-inventory-table">
          <caption className="sr-only">Advertising formats by platform</caption>
          <thead>
            <tr className={`border-b ${hair}`}>
              <th scope="col" className={`text-left py-2 pr-4 text-[11px] uppercase tracking-wider ${dim}`}>Format</th>
              {a.inventoryPlatforms.map((p) => (
                <th key={p} scope="col" className={`text-center py-2 px-2 text-[11px] uppercase tracking-wider ${dim}`}>{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {a.inventory.map((r) => (
              <tr key={r.format} className={`border-b ${hair}`}>
                <th scope="row" className={`text-left py-2 pr-4 font-medium ${ink}`}>{r.format}</th>
                {a.inventoryPlatforms.map((p) => (
                  <td key={p} className="py-2 px-2 text-center">
                    {r.status === "ok" ? (
                      <Check className="w-4 h-4 inline text-emerald-500" aria-label="Supported" />
                    ) : (
                      <span className={`text-[11px] font-semibold ${dim}`}>TBD</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={`mt-3 text-[12.5px] ${dim}`}>{a.inventoryNote}</p>
    </div>
  );
};

const Sales = ({ a }) => {
  const { brand, ink, muted, surface } = usePal();
  return (
    <div>
      <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pal-sales-steps">
        {a.salesSteps.map((s, i) => (
          <li key={s.title} className={`rounded-2xl border p-4 ${surface}`} data-testid={`pal-sales-step-${i}`}>
            <div className="flex items-center justify-between">
              <PalIcon name={s.iconName} className="w-5 h-5" style={{ color: brand }} />
              <span className={`text-[11px] font-semibold tabular-nums ${muted}`}>{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className={`mt-3 text-[15px] font-semibold leading-snug ${ink}`}>{s.title}</div>
            <div className={`mt-1 text-[12.5px] leading-snug ${muted}`}>{s.desc}</div>
          </li>
        ))}
      </ol>
      <p className={`mt-5 text-[14px] ${muted}`}>{a.salesNote}</p>
    </div>
  );
};

const Chips = ({ title, items, Icon, color }) => {
  const { ink, hair } = usePal();
  return (
    <div>
      <SubHead>{title}</SubHead>
      <ul className="mt-3 flex flex-wrap gap-2">
        {items.map((x) => (
          <li key={x} className={`inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-full border ${hair} ${ink}`}>
            <Icon className="w-3.5 h-3.5" style={{ color }} aria-hidden="true" />{x}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Workflow = ({ a }) => {
  const { brand, ink, muted, isDark, airline } = usePal();
  return (
    <div>
      <div className="overflow-x-auto">
      <ol className="relative grid grid-cols-7 min-w-[760px]" data-testid="pal-workflow">
        <span className={`absolute top-6 left-[7%] right-[7%] h-0.5 ${isDark ? "bg-white/10" : "bg-black/10"}`} aria-hidden="true" />
        {a.workflow.map((w) => (
          <li key={w.title} className="relative flex flex-col items-center text-center px-1">
            <span className="w-12 h-12 rounded-full flex items-center justify-center border-2" style={{ borderColor: brand, backgroundColor: isDark ? "#0b0b0d" : "#ffffff" }}>
              <PalIcon name={w.iconName} className="w-5 h-5" style={{ color: brand }} />
            </span>
            <span className={`mt-3 text-[14px] font-semibold ${ink}`}>{w.title}</span>
            <span className={`mt-0.5 text-[12px] leading-snug ${muted}`}>{w.desc}</span>
          </li>
        ))}
      </ol>
      </div>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Chips title="Exceptions handled" items={a.exceptions} Icon={AlertTriangle} color="#F59E0B" />
        <Chips title={`${airline.shortName} controls`} items={a.controls} Icon={ShieldCheck} color={brand} />
      </div>
    </div>
  );
};

const Costs = ({ a }) => {
  const { brand, ink, muted, dim, hair } = usePal();
  return (
    <div className="space-y-8">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pal-costs">
        {a.costs.map((x) => (
          <div key={x.title} className={`flex items-baseline justify-between gap-4 border-b pb-3 ${hair}`}>
            <dt className={`text-[15px] font-semibold ${ink}`}>{x.title}</dt>
            <dd className="text-[14px] font-semibold" style={{ color: brand }}>{x.desc}</dd>
          </div>
        ))}
      </dl>
      <div>
        <SubHead>Reporting</SubHead>
        <p className={`mt-2 text-[14px] ${muted}`}>{a.reportingScope}</p>
        <ol className="mt-4 flex flex-wrap items-center gap-2" data-testid="pal-reporting-stages">
          {a.reportingStages.map((s, i) => (
            <li key={s.title} className="flex items-center gap-2" title={s.desc}>
              <span className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border ${hair} ${ink}`}>{s.title}</span>
              {i < a.reportingStages.length - 1 && <span className={dim} aria-hidden="true">›</span>}
            </li>
          ))}
        </ol>
      </div>
      <p className={`text-[14px] ${muted}`}><span className={`font-semibold ${ink}`}>Reviews:</span> {a.reviews.join(" · ")}</p>
    </div>
  );
};

const PANELS = [Inventory, Sales, Workflow, PalEconomics, Costs];

const PalAdvertising = () => {
  const { c } = usePal();
  const a = c.advertising;
  const [tab, setTab] = useState(0);
  const Panel = PANELS[tab];
  return (
    <section aria-label={a.eyebrow} data-testid="pal-advertising">
      <PalHead eyebrow={a.eyebrow} title={a.title} accentLine={a.accentLine} lead={a.lead} />
      <div className="mt-8">
        <PalTabs tabs={a.tabs} active={tab} onChange={setTab} idPrefix="pal-ad" />
        <TabPanel idPrefix="pal-ad" index={tab}><Panel a={a} /></TabPanel>
      </div>
    </section>
  );
};

export default PalAdvertising;
